import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPublicCollection } from '../../services/publicCollectionsService';
import { useAuth } from '../../context/AuthContext';
import { getAppCtaHref } from '../../config/appConfig';
import styles from './PublicCollectionLanding.module.css';
import CoinMedia from '../../components/CoinMedia/CoinMedia';
import { resolveCoinMedia } from '../../utils/coinMedia';
import { SeasonalBanner } from '../../components/SeasonalAvailability/SeasonalAvailability';
import { CollectionCollectorStats } from '../../components/CollectorStats/CollectorStats';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://aluvo.app';

export default function PublicCollectionLanding() {
    const { slug } = useParams();
    const location = useLocation();
    const { user, loading: authLoading } = useAuth();
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    // Signed-in collectors belong on their own progress page, not the public
    // sign-up pitch. Signed-out visitors and crawlers stay on this page.
    const redirectToApp = !authLoading && Boolean(user);

    useEffect(() => {
        if (authLoading || redirectToApp) return;
        setLoading(true);
        setNotFound(false);
        getPublicCollection(slug)
            .then((res) => {
                if (res) setCollection(res);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug, authLoading, redirectToApp]);

    if (redirectToApp) {
        // Keep the query string (utm_* from emails / shares). First-touch
        // attribution is captured on app load, before this redirect.
        return <Navigate to={`/app/collections/${encodeURIComponent(slug)}${location.search}`} replace />;
    }

    if (authLoading || loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.skeleton}>
                        <div className={styles.skeletonTitle} />
                        <div className={styles.skeletonText} />
                        <div className={styles.skeletonGrid}>
                            {[1, 2, 3].map((i) => (
                                <div key={i} className={styles.skeletonCard} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (notFound || !collection) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.notFound}>
                        <h1>Collection not found</h1>
                        <p>This collection doesn't exist or is no longer available.</p>
                        <Link to="/discover" className={styles.ctaButton}>
                            Browse Collections
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const canonicalUrl = `${SITE_URL}/collections/${collection.slug}`;
    const metaTitle = `${collection.name} Coin & Films | Aluvo`;
    const filmTitles = collection.items.slice(0, 3).map((i) => i.title).join(', ');
    const metaDescription =
        collection.description ||
        `Verify ${filmTitles}${collection.total > 3 ? ` and ${collection.total - 3} more` : ''} to unlock ${collection.reward.name} on Aluvo.`;
    const rewardMedia = resolveCoinMedia(collection.reward);
    // og:image is always still artwork — the poster for a Signature coin.
    const ogImage = rewardMedia.staticUrl || `${SITE_URL}/og-image.png`;

    // Schema.org ItemList structured data
    const itemListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: collection.name,
        description: metaDescription,
        numberOfItems: collection.total,
        itemListElement: collection.items.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.title,
            description: item.blurb || undefined,
            image: item.posterUrl || undefined,
        })),
    };

    const ctaHref = getAppCtaHref();

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription.slice(0, 160)} />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription.slice(0, 160)} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:alt" content={`${collection.reward.name} — ${collection.name}`} />
                <meta property="og:site_name" content="Aluvo" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription.slice(0, 160)} />
                <meta name="twitter:image" content={ogImage} />
                <meta name="twitter:site" content="@aluvohq" />

                {/* Schema.org structured data */}
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchema)}
                </script>
            </Helmet>

            <div className={styles.page}>
                {/* ── Hero Header ── */}
                <header className={styles.hero}>
                    <div className={styles.container}>
                        <div className={styles.heroInner}>
                            <div className={styles.heroText}>
                                <p className={styles.eyebrow}>Aluvo Collection</p>
                                <h1 className={styles.collectionName}>{collection.name}</h1>
                                {collection.description && (
                                    <p className={styles.collectionDescription}>{collection.description}</p>
                                )}
                                <SeasonalBanner collection={collection} />
                                <div className={styles.rewardBadge}>
                                    {rewardMedia.posterUrl && (
                                        <img
                                            src={rewardMedia.posterUrl}
                                            alt={`${collection.reward.name} coin`}
                                            className={styles.rewardCoinThumb}
                                        />
                                    )}
                                    <div>
                                        <span className={styles.rewardLabel}>
                                            {rewardMedia.type === 'ANIMATED' ? '💎 Signature Coin reward' : 'Unlock reward'}
                                        </span>
                                        <span className={styles.rewardName}>{collection.reward.name}</span>
                                    </div>
                                </div>
                                <CollectionCollectorStats collection={collection} />
                            </div>
                            {rewardMedia.posterUrl && (
                                <div className={styles.heroCoin}>
                                    {/* Poster paints immediately; a Signature coin then loops (muted) */}
                                    <CoinMedia
                                        coin={collection.reward}
                                        animate
                                        alt={`${collection.reward.name} — ${collection.name} reward coin`}
                                        className={styles.heroCoinImage}
                                    />
                                    <p className={styles.heroCoinLabel}>{collection.reward.name}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className={styles.container}>
                    {/* ── Curatorial Note ── */}
                    {collection.curatorialNote && (
                        <section className={styles.editorialSection}>
                            <h2 className={styles.editorialHeading}>About This Collection</h2>
                            <p className={styles.editorialBody}>{collection.curatorialNote}</p>
                        </section>
                    )}

                    {/* ── Film List ── */}
                    <section className={styles.filmsSection} aria-label="Films in this collection">
                        <h2 className={styles.sectionHeading}>
                            {collection.total} Required {collection.total === 1 ? 'Title' : 'Titles'}
                        </h2>
                        <ol className={styles.filmList}>
                            {collection.items.map((item, idx) => (
                                <li key={item.tmdbId} className={styles.filmCard}>
                                    <div className={styles.filmPosterWrapper}>
                                        {item.posterUrl ? (
                                            <img
                                                src={item.posterUrl}
                                                alt={`${item.title} poster`}
                                                className={styles.filmPoster}
                                                loading={idx < 3 ? 'eager' : 'lazy'}
                                            />
                                        ) : (
                                            <div className={styles.filmPosterPlaceholder}>
                                                <span>{item.title.slice(0, 1)}</span>
                                            </div>
                                        )}
                                        {/*<span className={styles.filmNumber}>{idx + 1}</span>*/}
                                    </div>
                                    <div className={styles.filmInfo}>
                                        <h3 className={styles.filmTitle}>{item.title}</h3>
                                        {item.year && (
                                            <p className={styles.filmYear}>{item.year}</p>
                                        )}
                                        {item.description && (
                                            <p className={styles.filmDescription}>{item.description}</p>
                                        )}
                                        {item.blurb && (
                                            <p className={styles.filmBlurb}>{item.blurb}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>

                    {/* ── CTA Section ── */}
                    <section className={styles.ctaSection}>
                        <div className={styles.ctaCard}>
                            {rewardMedia.posterUrl && (
                                <img
                                    src={rewardMedia.posterUrl}
                                    alt={collection.reward.name}
                                    className={styles.ctaCoinImage}
                                />
                            )}
                            <h2 className={styles.ctaHeading}>
                                Verify all {collection.total}{' '}
                                {collection.items.some((i) => i.kind === 'tv') && collection.items.some((i) => i.kind === 'movie')
                                    ? 'titles'
                                    : collection.items.some((i) => i.kind === 'tv')
                                        ? 'shows'
                                        : 'films'}{' '}
                                on Aluvo to unlock{' '}
                                <span className={styles.ctaCoinName}>{collection.reward.name}</span>
                            </h2>
                            <p className={styles.ctaSubtext}>
                                Take a challenge for each title. Score high enough to earn a coin. Complete the
                                collection to unlock the exclusive reward.
                            </p>
                            <div className={styles.ctaButtons}>
                                <Link to="/register" className={styles.ctaButtonPrimary}>
                                    Start Collecting — It's Free
                                </Link>
                                {ctaHref && (
                                    <a
                                        href={ctaHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.ctaButtonSecondary}
                                    >
                                        Download the App
                                    </a>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
}
